import { createSelector, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { getUnixTime } from 'date-fns';
import { selectAllObjects } from './data/objectsSlice';
import formatBytes from '../../utils/formatBytes';
import { selectMe } from './data/userSlice';
import { selectActiveLanguage, selectDefaultLanguageId, selectTranslations } from './data/localizationSlice';
import { translate } from '../../hooks/useTranslations';

export const persistFavoriteLists = ({ favoriteLists }) => {
	localStorage.setItem('favoriteLists', JSON.stringify(favoriteLists));
};

export const getPersistedFavoriteLists = () => {
	const persistedData = localStorage.getItem('favoriteLists');
	if (persistedData) {
		return JSON.parse(persistedData);
	}
	return [];
};

const initialState = {
	favoriteLists: getPersistedFavoriteLists(),
	favoritesFormModalIsOpen: false,
	activeObject: null,
	favoritesDeleteModalIsOpen: false,
	activeFavoriteListId: null,
};

const favoritesSlice = createSlice({
	name: 'favorites',
	initialState,
	reducers: {
		createFavoriteList: (state, action) => {
			if (action?.payload?.title) {
				const { title } = action.payload;

				const favList = { id: uuidv4(), title, objectIds: state.activeObject?.id ? [state.activeObject?.id] : [], created: getUnixTime(Date.now()) };
				state.favoriteLists.push(favList);
			}
		},
		removeFavoriteList: (state, action) => {
			if (action?.payload?.favoriteListId) {
				const { favoriteListId } = action.payload;

				state.favoriteLists = state.favoriteLists.filter((favList) => favList.id !== favoriteListId);
			}
		},
		editTitleFavoriteList: (state, action) => {
			if (action?.payload?.title && action?.payload?.favoriteListId) {
				const { title, favoriteListId } = action.payload;
				const currentFavListIndex = state.favoriteLists.findIndex((favList) => favList.id === favoriteListId);
				if (currentFavListIndex > -1) {
					const currentFavList = state.favoriteLists[currentFavListIndex];
					const newFavList = { ...currentFavList, title };
					state.favoriteLists[currentFavListIndex] = newFavList;
				}
			}
		},
		addObjectIdToFavoriteList: (state, action) => {
			if (action?.payload?.objectId && action?.payload?.favoriteListId) {
				const { objectId, favoriteListId } = action.payload;

				const currentFavListIndex = state.favoriteLists.findIndex((favList) => favList.id === favoriteListId);
				if (currentFavListIndex > -1) {
					const currentFavList = state.favoriteLists[currentFavListIndex];
					if (currentFavList?.objectIds) {
						const oldObjectIds = currentFavList?.objectIds;
						if (!oldObjectIds.includes(objectId)) {
							oldObjectIds.push(objectId);
							const newFavList = { ...currentFavList, objectIds: oldObjectIds };
							state.favoriteLists[currentFavListIndex] = newFavList;
						}
					}
				}
			}
		},
		removeObjectIdFromFavoriteList: (state, action) => {
			if (action?.payload?.objectId && action?.payload?.favoriteListId) {
				const { objectId, favoriteListId } = action.payload;

				const currentFavListIndex = state.favoriteLists.findIndex((favList) => favList.id === favoriteListId);
				if (currentFavListIndex > -1) {
					const currentFavList = state.favoriteLists[currentFavListIndex];
					if (currentFavList?.objectIds) {
						const oldObjectIds = currentFavList?.objectIds;
						if (oldObjectIds.includes(objectId)) {
							const newObjectIds = oldObjectIds.filter((objId) => objId !== objectId);
							const newFavList = { ...currentFavList, objectIds: newObjectIds };
							state.favoriteLists[currentFavListIndex] = newFavList;
						}
					}
				}
			}
		},
		reorderObjectIdInFavoriteList: (state, action) => {
			if (action?.payload?.objectId && action?.payload?.favoriteListId && action?.payload?.newIndex) {
				const { objectId, favoriteListId } = action.payload; // TODO: newIndex

				const currentFavListIndex = state.favoriteLists.findIndex((favList) => favList.id === favoriteListId);
				if (currentFavListIndex > -1) {
					const currentFavList = state.favoriteLists[currentFavListIndex];
					if (currentFavList?.objectIds) {
						const oldObjectIds = currentFavList?.objectIds;
						if (oldObjectIds.includes(objectId)) {
							const newObjectIds = oldObjectIds; // TODO: move objectId to new indexx
							const newFavList = { ...currentFavList, objectIds: newObjectIds };
							state.favoriteLists[currentFavListIndex] = newFavList;
						}
					}
				}
			}
		},
		openFavoritesDeleteModal: (state, action) => {
			if (action?.payload?.favoriteListId) {
				const { favoriteListId } = action.payload;

				state.activeFavoriteListId = favoriteListId;
			}

			state.favoritesDeleteModalIsOpen = true;
		},
		closeFavoritesDeleteModal: (state) => {
			state.activeFavoriteListId = null;
			state.favoritesDeleteModalIsOpen = false;
		},
		openFavoritesFormModal: (state, action) => {
			if (action?.payload?.object) {
				const { object } = action.payload;

				state.activeObject = object;
			}

			state.favoritesFormModalIsOpen = true;
		},
		closeFavoritesFormModal: (state) => {
			state.activeObject = null;
			state.favoritesFormModalIsOpen = false;
		},
		setActiveObject: (state, action) => {
			if (action?.payload?.object) {
				const { object } = action.payload;

				state.activeObject = object;
			}
		},
		unsetActiveObject: (state) => {
			state.activeObject = null;
		},
		setFavoriteLists: (state, action) => {
			if (action?.payload?.favoriteLists) {
				const { favoriteLists } = action.payload;

				state.favoriteLists = favoriteLists;
			}
		},
		replaceFavoriteList: (state, action) => {
			if (action?.payload?.favoriteList) {
				const { favoriteList } = action.payload;

				const currentFavListIndex = state.favoriteLists.findIndex((favList) => favList.id === favoriteList.id);
				if (currentFavListIndex > -1) {
					state.favoriteLists[currentFavListIndex] = favoriteList;
				}
			}
		},
	},
});

export default favoritesSlice.reducer;
export const {
	setFavoriteLists, createFavoriteList, removeFavoriteList, editTitleFavoriteList, addObjectIdToFavoriteList, removeObjectIdFromFavoriteList, openFavoritesDeleteModal, closeFavoritesDeleteModal, openFavoritesFormModal, closeFavoritesFormModal, setActiveObject, unsetActiveObject, replaceFavoriteList,
} = favoritesSlice.actions;

export const selectFavoriteLists = (state) => state.favorites.favoriteLists;
export const selectFavoritesFormModalIsOpen = (state) => state.favorites.favoritesFormModalIsOpen;
export const selectActiveObject = (state) => state.favorites.activeObject;
export const selectFavoritesDeleteModalIsOpen = (state) => state.favorites.favoritesDeleteModalIsOpen;
export const selectActiveFavoriteListId = (state) => state.favorites.activeFavoriteListId;

export const selectFavoriteListById = (state, favoriteListId) => {
	const favoriteList = state.favorites.favoriteLists.find((favList) => favList.id === favoriteListId);
	return favoriteList;
};

export const selectFavoriteListByIdWithObjects = createSelector(
	selectFavoriteListById,
	selectAllObjects,
	(favoriteList, allObjects) => (favoriteList ? ({ ...favoriteList, objects: favoriteList.objectIds && allObjects ? favoriteList.objectIds.map((objId) => allObjects[objId]).filter((entry) => typeof entry !== 'undefined' && entry !== null) : []}) : null),
);

export const selectFavoriteListsWithObjects = createSelector(
	selectFavoriteLists,
	selectAllObjects,
	(favoriteLists, allObjects) => {
		const favoriteListsWithObjects = favoriteLists.map((favList) => {
			let favListSize = 0;
			const objects = favList.objectIds && allObjects ? favList.objectIds.map((objId) => {
				const object = allObjects[objId];
				if (object) {
					favListSize += parseFloat(object.size, 10);
					return object;
				}
				return null;
			}).filter((entry) => typeof entry !== 'undefined' && entry !== null) : [];
			const favListWithObjects = { ...favList, objects, size: formatBytes(favListSize) };
			return favListWithObjects;
		});
		return favoriteListsWithObjects;
	},
);

export const selectIPadFavoriteLists = createSelector(
	selectMe,
	selectAllObjects,
	selectTranslations,
	selectActiveLanguage,
	selectDefaultLanguageId,
	(me, allObjects, allTranslations, activeLanguageId, defaultLanguageId) => {
		if (me?.backup_json?.favoriteLists) {
			const iPadFavLists = JSON.parse(me?.backup_json?.favoriteLists);
			if (iPadFavLists && Object.values(iPadFavLists)) {
				const iPadFavListsWithObjects = Object.values(iPadFavLists).map((favList) => {
					let favListSize = 0;
					const objects = favList.objectIds && allObjects ? favList.objectIds.map((objId) => {
						const object = allObjects[objId];
						if (object) {
							favListSize += parseFloat(object.size, 10);
							return object;
						}
						if (objId.includes('ifu-')) {
							return {
								id: objId,
								title1: translate('error_file_not_found_title', allTranslations, activeLanguageId, defaultLanguageId),
								subtitle: translate('ifu_headline', allTranslations, activeLanguageId, defaultLanguageId),
								type: 'doc',
								file: '',
							};
						}
						if (objId.includes('mf-')) {
							return {
								id: objId,
								title1: translate('error_file_not_found_title', allTranslations, activeLanguageId, defaultLanguageId),
								subtitle: translate('my_files_headline', allTranslations, activeLanguageId, defaultLanguageId),
								type: 'doc',
								file: '',
							};
						}
						return null;
					}).filter((entry) => typeof entry !== 'undefined' && entry !== null) : [];
					return {
						id: favList.id, title: favList.title, objectIds: favList.objectIds, objects, size: formatBytes(favListSize), created: parseInt(favList.createdAt, 10),
					};
				});
				return iPadFavListsWithObjects;
			}
		}
		return [];
	},
);

export const selectIPadFavoriteListById = createSelector(
	selectIPadFavoriteLists,
	(_state, favListId) => favListId,
	(allIPadFavoriteLists, favListId) => {
		const iPadFavList = allIPadFavoriteLists.find((favList) => favList.id === favListId);
		return iPadFavList;
	},
);
